import { useState } from "react";
import { ChevronDownIcon } from "lucide-react";

import { ResumeCard } from "./ResumeCard";
import { StafferCallsList } from "./StafferCallsList";
import { VoiceAiCallsList } from "./VoiceAiCallsList";
import { useUserSheet } from "../userSheetContext";
import { withRequiredFields } from "../withRequiredFields";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { PastApplicationAnswersDropdown } from "@/components/companies/jobs/candidate-sheet-v2/records/PastApplicationAnswersDropdown";

import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetOtherCandidacies } from "@/hooks/queries/useGetOtherCandidacies";
import { pastApplications as pastApplicationsLib } from "@/lib/pastApplications";
import { sort } from "@/lib/sort";
import { cn } from "@/lib/utils";

export const RecordsTab = () => {
  /* Context */
  const { candidacyId, contentPaddingClassName, jobId, workerId } =
    useUserSheet();

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const callHistoryQuery = useGetCallHistory(workerId);
  const otherCandidaciesQuery = useGetOtherCandidacies({
    candidacyId,
    talentId: workerId,
  });

  /* React Hooks */
  const [isVoiceAiCallsOpen, setIsVoiceAiCallsOpen] = useState(false);
  const [isStafferCallsOpen, setIsStafferCallsOpen] = useState(false);

  /* Helpers */
  const profile = userApi.data;
  const candidateFirstName = profile?.profile.firstName ?? "Candidate";
  const primaryResumeId = profile?.primaryResumeId ?? null;
  const resumeFiles = profile
    ? sort.sortResumeFilesDesc(
        withRequiredFields(profile.resumeFiles, ["id", "title"]),
      )
    : [];
  const stafferCalls = callHistoryQuery.data?.stafferCalls ?? [];
  const voiceAiCalls = callHistoryQuery.data?.aiCalls ?? [];
  const pastApplications = pastApplicationsLib.fromOtherCandidacies(
    otherCandidaciesQuery.data,
  );

  /* Render */
  const renderSectionHeader = (title: string, count: number) => {
    return (
      <Row className="items-center gap-2">
        <Text className="text-sm font-medium text-slate-800">{title}</Text>
        <Text className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
          {count}
        </Text>
      </Row>
    );
  };

  const renderCallsSectionHeader = ({
    count,
    isOpen,
    onToggle,
    title,
  }: {
    count: number;
    isOpen: boolean;
    onToggle: () => void;
    title: string;
  }) => {
    return (
      <Button
        className="h-auto w-full justify-between bg-transparent p-0 hover:bg-transparent"
        onClick={onToggle}
        type="button"
        variant="ghost"
      >
        {renderSectionHeader(title, count)}
        <ChevronDownIcon
          className={cn(
            "h-4 w-4 text-slate-400 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </Button>
    );
  };

  const renderResumeSection = () => {
    return (
      <Column className="gap-5">
        {renderSectionHeader("Resume", resumeFiles.length)}
        {resumeFiles.length ? (
          <Row className="flex-wrap gap-4">
            {resumeFiles.map((resumeFile) => (
              <ResumeCard
                isPrimary={resumeFile.id === primaryResumeId}
                key={resumeFile.id}
                onResumeUpdated={() => {
                  void userApi.refetch();
                }}
                primaryResumeId={primaryResumeId}
                resumeFile={resumeFile}
                userId={workerId}
              />
            ))}
          </Row>
        ) : (
          <Text className="text-sm italic text-slate-400">
            No resume on file
          </Text>
        )}
      </Column>
    );
  };

  // Search candidates have no application on this job, so the section is only
  // the past-apps pill — hidden entirely when there is nothing to show.
  const renderApplicationAnswersSection = () => {
    if (!pastApplications.length) return null;

    return (
      <Row className="items-center gap-2">
        <Text className="text-sm font-medium text-slate-800">
          Application Answers
        </Text>
        <PastApplicationAnswersDropdown pastApplications={pastApplications} />
      </Row>
    );
  };

  const render = () => {
    return (
      <Column className={cn("gap-8 py-7", contentPaddingClassName)}>
        {renderApplicationAnswersSection()}
        {renderResumeSection()}
        <Column className="gap-5">
          {renderCallsSectionHeader({
            count: voiceAiCalls.length,
            isOpen: isVoiceAiCallsOpen,
            onToggle: () => {
              setIsVoiceAiCallsOpen((isOpen) => !isOpen);
            },
            title: "AI calls",
          })}
          {isVoiceAiCallsOpen ? (
            <VoiceAiCallsList
              calls={voiceAiCalls}
              candidateFirstName={candidateFirstName}
              isError={callHistoryQuery.isError}
              isPending={callHistoryQuery.isPending}
            />
          ) : null}
        </Column>
        <Column className="gap-5">
          {renderCallsSectionHeader({
            count: stafferCalls.length,
            isOpen: isStafferCallsOpen,
            onToggle: () => {
              setIsStafferCallsOpen((isOpen) => !isOpen);
            },
            title: "Staffer calls",
          })}
          {isStafferCallsOpen ? (
            <StafferCallsList
              calls={stafferCalls}
              candidateFirstName={candidateFirstName}
              isError={callHistoryQuery.isError}
              isPending={callHistoryQuery.isPending}
            />
          ) : null}
        </Column>
      </Column>
    );
  };

  return render();
};
